import Message from 'components/Message';
import {
	useGlobalMemberPopoversContext,
	useGlobalPopoversContext,
} from 'contexts';
import { useIsCompanySite } from 'hooks';
import type { OIDCError } from 'utils/business-logic';
import { useI18n } from 'utils/i18n';
import { log } from 'utils/log';

interface Props {
	className?: string;
	error?: OIDCError;
	onDismiss?: () => void;
}

export default function AuthError({ className, error, onDismiss }: Props) {
	const { t } = useI18n();
	const isCompanySite = useIsCompanySite();
	const { openContactForm } = useGlobalPopoversContext();
	const { closeLogin, closeSignUp, openSignUp } =
		useGlobalMemberPopoversContext();

	if (!error) {
		return null;
	}
	const commonProps = {
		className,
		onDismiss,
		variant: 'error' as const,
	};

	switch (error) {
		case 'x-UserNotFound':
			return (
				<Message
					{...commonProps}
					text={
						isCompanySite
							? t('account_login_error_not_registered_pro')
							: t('account_login_error_not_registered_club')
					}
					onButtonClick={
						isCompanySite
							? undefined
							: () => {
									closeLogin();
									openSignUp();
								}
					}
					buttonText={t('auth_error_become_member_button')}
				/>
			);
		case 'x-UserError':
			return (
				<Message
					{...commonProps}
					text={t('account_login_error_profile_locked_text')}
					onButtonClick={() => {
						closeLogin();
						closeSignUp();
						openContactForm();
					}}
					buttonText={t('auth_error_open_contact_form_button')}
				/>
			);
		case 'x-ServerError':
		case 'internal-error-token':
		case 'internal-error-unknown':
		case 'x-Unknown':
			return (
				<Message
					{...commonProps}
					text={t('account_login_error_try_again_text')}
				/>
			);
		case 'x-BirthDateRequired':
		case 'x-ExpiredCredentials':
		case 'x-InvalidCredentials':
		case 'x-UserCancel':
			return null;
		default:
			log.warning('Unhandled Connect auth error:', error);
			return null;
	}
}
AuthError.displayName = 'AuthError';
